/**
 * The TikTok settings of one post (TikTok Content Posting API, Direct Post).
 *
 * TikTok's "Content Sharing Guidelines" (checked 2026-10-06,
 * https://developers.tiktok.com/doc/content-sharing-guidelines) bind every Direct Post app:
 * - the posting page shows the creator's nickname (and avatar) from `creator_info`, read when the
 *   page opens, and stops when the creator cannot post now;
 * - the user picks the privacy level from the `creator_info` options; there is NO default;
 * - "Allow comments / Duet / Stitch" are off by default; an option that `creator_info` disables is
 *   greyed out; a photo post has only comments;
 * - the commercial content switch is off by default; "Your brand" gives the label "Promotional
 *   content", "Branded content" gives "Paid partnership"; branded content cannot be private
 *   ("Only me"); the post button stays disabled while the switch is on with no option;
 * - the consent line "By posting, you agree to TikTok's Music Usage Confirmation" (plus the
 *   Branded Content Policy for branded content) shows before the post button;
 * - the video duration respects `max_video_post_duration_sec`;
 * - no promotional watermark on the content.
 *
 * The campaign questions ("Schedule all N posts", `campaignLaunch.ts`) follow the same rules: the
 * user picks the level one time for the TikTok posts of the campaign in front of them, and no
 * answer is stored or selected for a later campaign.
 *
 * The web app, the server and the tests read this file. iOS and Android carry a copy of the rules
 * (`TikTokSettingsRules.swift`, `TikTokSettingsRules.kt`); change the three together. No imports.
 */

/** The privacy levels of TikTok, the widest first. */
export const TIKTOK_PRIVACY_LEVELS = [
  'PUBLIC_TO_EVERYONE',
  'MUTUAL_FOLLOW_FRIENDS',
  'FOLLOWER_OF_CREATOR',
  'SELF_ONLY',
] as const;

export type TikTokPrivacyLevel = (typeof TIKTOK_PRIVACY_LEVELS)[number];

export function isTikTokPrivacyLevel(value: unknown): value is TikTokPrivacyLevel {
  return typeof value === 'string' && (TIKTOK_PRIVACY_LEVELS as readonly string[]).includes(value);
}

/** The TikTok pages that the consent line links. */
export const TIKTOK_MUSIC_USAGE_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en';
export const TIKTOK_BRANDED_CONTENT_POLICY_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en';

/**
 * The settings that the user chose for one TikTok post. Stored on the post
 * (`ScheduledPostModel.tiktokSettings`) and sent to TikTok at the publish.
 */
export interface TikTokPostSettings {
  privacyLevel: TikTokPrivacyLevel;
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  /** The commercial content switch ("Disclose post content"). */
  discloseContent: boolean;
  /** "Your brand": the creator promotes themself (TikTok `brand_organic_toggle`). */
  yourBrand: boolean;
  /** "Branded content": the creator promotes a third party (TikTok `brand_content_toggle`). */
  brandedContent: boolean;
}

/** The form state: the privacy level stays null until the user picks one (no default). */
export interface TikTokSettingsDraft extends Omit<TikTokPostSettings, 'privacyLevel'> {
  privacyLevel: TikTokPrivacyLevel | null;
}

export const EMPTY_TIKTOK_SETTINGS: TikTokSettingsDraft = {
  privacyLevel: null,
  allowComment: false,
  allowDuet: false,
  allowStitch: false,
  discloseContent: false,
  yourBrand: false,
  brandedContent: false,
};

/** The creator, as `creator_info/query` answered it right now. */
export interface TikTokCreatorInfo {
  nickname: string;
  username: string | null;
  /** Valid about 2 hours (TikTok). */
  avatarUrl: string | null;
  privacyLevelOptions: TikTokPrivacyLevel[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoPostDurationSec: number | null;
}

/**
 * Why the creator cannot post now.
 * - `tooManyPosts`: `spam_risk_too_many_posts` (about 15 posts a day, shared by every app).
 * - `bannedFromPosting`: `spam_risk_user_banned_from_posting`.
 * - `activeUserCap`: `reached_active_user_cap` (the daily cap of the app).
 * - `noPrivacyOptions`: `creator_info` offered no privacy level.
 * - `reconnect`: TikTok refused the login; connect the channel again in Channels.
 * - `unavailable`: TikTok gave another error; try again later.
 */
export type TikTokCreatorBlock =
  | 'tooManyPosts'
  | 'bannedFromPosting'
  | 'activeUserCap'
  | 'noPrivacyOptions'
  | 'reconnect'
  | 'unavailable';

/**
 * `GET /accounts/:accountId/promo/channels/tiktok/creator-info[?mediaFileId=]`.
 * `creator` is null when TikTok answered with an error (then `blockedReason` says why).
 */
export interface TikTokCreatorInfoAnswer {
  creator: TikTokCreatorInfo | null;
  canPost: boolean;
  blockedReason: TikTokCreatorBlock | null;
  /** Always true since TikTok audited the app (2026-10). Kept: shipped apps decode it. */
  auditedApp: boolean;
  /** The attached file, when the request named one. */
  media: { watermarked: boolean; durationSec: number | null } | null;
}

/** The reasons that a TikTok post cannot go out with these settings. */
export type TikTokSettingsIssue =
  | 'creatorBlocked'
  | 'privacyMissing'
  | 'privacyNotOffered'
  | 'brandedPrivate'
  | 'disclosureEmpty'
  | 'videoTooLong'
  | 'watermarked';

export interface TikTokSettingsContext {
  /** Null while `creator_info` loads or failed. */
  creator: TikTokCreatorInfo | null;
  canPost: boolean;
  mediaKind: 'image' | 'video' | null;
  /** Null when the duration is not known. */
  durationSec: number | null;
  watermarked: boolean;
}

/** True when the option cannot be picked: branded content cannot be private. */
export function isTikTokPrivacyLevelDisabled(
  level: TikTokPrivacyLevel,
  draft: Pick<TikTokSettingsDraft, 'discloseContent' | 'brandedContent'>
): boolean {
  return level === 'SELF_ONLY' && draft.discloseContent && draft.brandedContent;
}

/** The label that TikTok puts on the post, or null. Branded content wins over your brand. */
export function tiktokCommercialLabel(
  draft: Pick<TikTokSettingsDraft, 'discloseContent' | 'yourBrand' | 'brandedContent'>
): 'promotional' | 'paidPartnership' | null {
  if (!draft.discloseContent) {
    return null;
  }
  if (draft.brandedContent) {
    return 'paidPartnership';
  }
  return draft.yourBrand ? 'promotional' : null;
}

/** The consent line: the Music Usage Confirmation always, the Branded Content Policy for branded content. */
export function tiktokConsentNamesBrandedPolicy(
  draft: Pick<TikTokSettingsDraft, 'discloseContent' | 'brandedContent'>
): boolean {
  return draft.discloseContent && draft.brandedContent;
}

/** Duet and Stitch exist for a video only. */
export function tiktokShowsDuetAndStitch(mediaKind: 'image' | 'video' | null): boolean {
  return mediaKind !== 'image';
}

/** Every reason that stops the post, in the order the form shows them. Empty: the post can go. */
export function tiktokSettingsIssues(
  draft: TikTokSettingsDraft,
  context: TikTokSettingsContext
): TikTokSettingsIssue[] {
  const issues: TikTokSettingsIssue[] = [];
  if (!context.canPost) {
    issues.push('creatorBlocked');
  }
  if (context.watermarked) {
    issues.push('watermarked');
  }
  if (draft.privacyLevel === null) {
    issues.push('privacyMissing');
  } else {
    if (context.creator !== null && !context.creator.privacyLevelOptions.includes(draft.privacyLevel)) {
      issues.push('privacyNotOffered');
    }
    if (draft.discloseContent && draft.brandedContent && draft.privacyLevel === 'SELF_ONLY') {
      issues.push('brandedPrivate');
    }
  }
  if (draft.discloseContent && !draft.yourBrand && !draft.brandedContent) {
    issues.push('disclosureEmpty');
  }
  const max = context.creator?.maxVideoPostDurationSec ?? null;
  if (context.mediaKind === 'video' && max !== null && context.durationSec !== null && context.durationSec > max) {
    issues.push('videoTooLong');
  }
  return issues;
}

/** The draft as stored settings, or null while the privacy level is not picked. */
export function tiktokSettingsFromDraft(draft: TikTokSettingsDraft): TikTokPostSettings | null {
  if (draft.privacyLevel === null) {
    return null;
  }
  const disclose = draft.discloseContent && (draft.yourBrand || draft.brandedContent);
  return {
    privacyLevel: draft.privacyLevel,
    allowComment: draft.allowComment,
    allowDuet: draft.allowDuet,
    allowStitch: draft.allowStitch,
    discloseContent: disclose,
    yourBrand: disclose && draft.yourBrand,
    brandedContent: disclose && draft.brandedContent,
  };
}

/** Stored settings back into the form (an edit of a post that has them). */
export function tiktokDraftFromSettings(settings: TikTokPostSettings | null): TikTokSettingsDraft {
  return settings === null ? { ...EMPTY_TIKTOK_SETTINGS } : { ...settings };
}

/**
 * Reads a stored value (the JSON column) back as settings, or null when it is not a full, valid
 * set. Never trusts the shape of the column.
 */
export function parseTikTokPostSettings(value: unknown): TikTokPostSettings | null {
  if (value === null || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!isTikTokPrivacyLevel(record.privacyLevel)) {
    return null;
  }
  const flags = ['allowComment', 'allowDuet', 'allowStitch', 'discloseContent', 'yourBrand', 'brandedContent'] as const;
  if (flags.some((flag) => typeof record[flag] !== 'boolean')) {
    return null;
  }
  return {
    privacyLevel: record.privacyLevel,
    allowComment: record.allowComment as boolean,
    allowDuet: record.allowDuet as boolean,
    allowStitch: record.allowStitch as boolean,
    discloseContent: record.discloseContent as boolean,
    yourBrand: record.yourBrand as boolean,
    brandedContent: record.brandedContent as boolean,
  };
}
